import {
  ACTION,
  BlotterTableExtrasMenu,
  Button,
  ButtonVariants,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Toggle,
  Tour,
  useBlotterTableExtrasMenu,
  useDynamicCallback,
  useMixpanel,
} from '@talos/kyoko';

import { useEmbeddedContext, useFeatureFlag, useRoleAuth } from '../../../hooks';

import { OMSView } from '@src/components/OMS/OMSView';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import type { Order, UseBlotterTable } from '@talos/kyoko';
import { useCallback, useMemo, type SetStateAction } from 'react';
import { openView } from '../../../components/OMS/OMSSlice';
import { TourName } from '../../../tokens/tours';
import type { BlotterTableOrderFilter } from './useOrderFilter';

type OrderOptionsProps = {
  changeFilter: (action: SetStateAction<BlotterTableOrderFilter>) => void;
  filter: BlotterTableOrderFilter;
  handleCloneTab?: () => void;
  tabType: 'FilteredOrders' | 'OpenOrders';
  alwaysShowDDHOrders?: boolean;
  blotterTable: UseBlotterTable<Order>;
  handleExport?: () => void;
};

export function OrderOptions({
  filter,
  changeFilter,
  handleCloneTab,
  tabType,
  alwaysShowDDHOrders,
  handleExport,
  blotterTable,
}: OrderOptionsProps) {
  const mixpanel = useMixpanel();

  const { isAuthorized } = useRoleAuth();
  const dispatch = useAppStateDispatch();
  const { enableDynamicDeltaHedging, enableArchiveOrders } = useFeatureFlag();
  const { isEmbedded } = useEmbeddedContext();

  const handleImport = () => {
    mixpanel.track(MixpanelEvent.OpenOrderImportForm);
    dispatch(openView(OMSView.OrderImport));
  };

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleOnApiOrdersChange = useDynamicCallback((value: boolean) => {
    mixpanel.track(MixpanelEvent.ApiOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
    changeFilter(curr => ({ ...curr, HideApiCalls: !value }));
  });

  const handleOnShowArchiveOrdersChange = useDynamicCallback((value: boolean) => {
    mixpanel.track(MixpanelEvent.ShowArchiveOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
    changeFilter(curr => ({ ...curr, ShowArchivedOrders: value }));
  });

  const handleOnShowDDHOrders = useCallback(
    (value: boolean) => {
      mixpanel.track(
        value ? MixpanelEvent.DynamicDeltaHedgingOrdersShown : MixpanelEvent.DynamicDeltaHedgingOrdersHidden
      );
      changeFilter(curr => ({ ...curr, ShowDDHOrders: value }));
    },
    [changeFilter, mixpanel]
  );

  const handleOnCloneTab: typeof handleCloneTab = useMemo(
    () =>
      handleCloneTab
        ? () => {
            mixpanel.track(MixpanelEvent.CloneTab);
            return handleCloneTab();
          }
        : undefined,
    [handleCloneTab, mixpanel]
  );

  // API orders toggle isn't hooked up to the orders provider which populated the OpenOrders blotter, so hide it
  const showAPIOrdersToggle = tabType !== 'OpenOrders';

  return (
    <div data-testid="order-options-menu">
      {enableDynamicDeltaHedging && !alwaysShowDDHOrders && <Tour tour={TourName.v249DDHOrderBlotterMenu} />}
      <BlotterTableExtrasMenu {...extrasMenuPopover}>
        {showAPIOrdersToggle && (
          <Toggle
            label="API Orders"
            size={FormControlSizes.Small}
            tooltip="Include orders submitted through the API in the results."
            checked={!filter.HideApiCalls}
            onChange={handleOnApiOrdersChange}
          />
        )}
        {enableArchiveOrders && (
          <Toggle
            label="Show Archived Orders"
            size={FormControlSizes.Small}
            tooltip="Show orders which have been archived."
            checked={filter?.ShowArchivedOrders === true}
            onChange={handleOnShowArchiveOrdersChange}
          />
        )}
        {enableDynamicDeltaHedging && !alwaysShowDDHOrders && (
          <Toggle
            label="Show DDH Orders"
            size={FormControlSizes.Small}
            tooltip="Show Order Level DDH Orders."
            checked={filter?.ShowDDHOrders === true}
            onChange={handleOnShowDDHOrders}
          />
        )}
        {handleOnCloneTab && !alwaysShowDDHOrders && (
          <Button
            startIcon={IconName.Duplicate}
            variant={ButtonVariants.Default}
            size={FormControlSizes.Small}
            onClick={handleOnCloneTab}
          >
            Clone Tab
          </Button>
        )}
        {isAuthorized(ACTION.SUBMIT_ORDER) && !isEmbedded && (
          <Button
            startIcon={IconName.DocumentUpload}
            variant={ButtonVariants.Default}
            size={FormControlSizes.Small}
            onClick={() => {
              extrasMenuPopover.close();
              handleImport();
            }}
          >
            Import Orders
          </Button>
        )}

        {tabType === 'OpenOrders' && (
          <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
        )}
        {tabType === 'FilteredOrders' && (
          <Button
            startIcon={IconName.DocumentDownload}
            size={FormControlSizes.Small}
            onClick={handleExport}
            data-testid="export-csv"
          >
            Export
          </Button>
        )}
      </BlotterTableExtrasMenu>
    </div>
  );
}
